import { Page } from "../../../components/page-base";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { RowActionButtons } from "../../../components/datagrid/RowActionButtons";
import { NameCell } from "../../../components/datagrid/NameCell";
import { iniciais } from "../../../components/datagrid/iniciais";
import {
  ChipButton,
  ChipGroupLabel,
  FilledButton,
  ToolbarDivider,
} from "../../../components/datagrid/ChipButton";
import { KpiStrip } from "../../../components/kpi-strip";
import { formatarNumero } from "../../../components/kpi-strip/formatar";
import { StatusChip } from "../../../components/datagrid/StatusChip";
import {
  BulkActionsBar,
  BulkAction,
} from "../../../components/datagrid/BulkActionsBar";
import type {
  DataGridColumn,
  DataGridRow,
  FilterChip,
} from "../../../components/datagrid/types";
import SearchIcon from "@mui/icons-material/Search";
import {
  ChipFilterConfig,
  FilterDrawer,
} from "../../../components/filter-drawer";
import { Container } from "./styles";
import { isMobile } from "is-mobile";
import { useState, useMemo, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { LevelChip } from "../../../components/datagrid/LevelChip";
import { SellerFormModal } from "../components/seller-form-modal";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import { toast } from "react-toastify";
import useUserStore from "../../../store/user";
import { SellersService } from "../services";
import { buildSellerPayload, type SellerFormData } from "./seller-payload";
import { hideImporterAccounts } from "./seller-visibility";
export interface Seller {
  id: string;
  name: string;
  email: string;
  level:
    | "Cliente"
    | "Supervisor de Contas"
    | "Conta Google (importador)"
    | "Admin"
    | "Vendedor";

  // O que a lista realmente grava (e o que o StatusChip pinta). O tipo dizia
  // "active"/"inactive", que nunca chegou a ser usado aqui - por isso o modal
  // de edicao precisava de cast para ler os dois formatos.
  status: "success" | "disabled";
  statusLabel: "Ativo" | "Inativo";
  /** Último login no painel (backend 1.13.2); nulo = nunca acessou desde 30/09/2026. */
  lastLoginAt?: string | null;
  /** Nível cru da API (ADMIN, STAFF, CUSTOMER...), usado no PUT de ativar/desativar. */
  userType: string;
}

export type TSeller = {
  id: string;
  nome: string;
  userType: string;
  email: string;
  indDisabled: boolean;
  lastLoginAt?: string | null;
};

type SellerFilters = {
  role: string;
  disabled: boolean | null;
};

const initialFilters: SellerFilters = {
  role: "",
  disabled: null,
};

export const Sellers = () => {
  const { user } = useUserStore();
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<(string | number)[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
  });
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const contarNivel = (nivel: Seller["level"]) => sellers.filter((u) => u.level === nivel).length;
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSeller, setEditingSeller] = useState<Seller | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [sellerToDeleteId, setSellerToDeleteId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submittedSearch, setSubmittedSearch] = useState("");

  const parseUserType = (userType: string) => {
    switch (userType) {
      case "CUSTOMER":
        return "Cliente";
      case "PORTFOLIO_MANAGER":
        return "Supervisor de Contas";
      case "GMAIL_MANAGER":
        return "Conta Google (importador)";
      case "ADMIN":
        return "Admin";
      case "STAFF":
        return "Vendedor";
      default:
        return "";
    }
  };
  const fetchData = useCallback(async () => {
    if (!user?.token) {
      toast.warn("Sessão expirada. Faça login novamente.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = {
        token: user.token,
        query: submittedSearch,
        role: filters.role === "" ? undefined : filters.role,
        disabled: filters.disabled,
      };

      const response = await SellersService.getSellers(params);
      const visibleResponse = hideImporterAccounts<TSeller>(
        response || [],
        filters.role,
      );

      if (visibleResponse.length > 0) {
        const mappedProfiles: Seller[] = visibleResponse.map((p: TSeller) => ({
          id: p.id,
          name: p.nome,
          email: p.email,
          // parseUserType devolve "" para nivel desconhecido (ex.: PLATFORM_READER),
          // e o chip fica vazio. Comportamento antigo, mantido de proposito aqui.
          level: parseUserType(p.userType) as Seller["level"],
          status: p.indDisabled ? "disabled" : "success",
          statusLabel: p.indDisabled ? "Inativo" : "Ativo",
          lastLoginAt: p.lastLoginAt ?? null,
          userType: p.userType,
        }));
        setSellers(mappedProfiles);
        setPagination((prev) => ({
          ...prev,
          totalCount: visibleResponse.length,
        }));
      } else {
        setSellers([]);
        setPagination((prev) => ({ ...prev, totalCount: 0 }));
      }
    } catch (error) {
      console.error("Erro ao buscar perfis:", error);
      toast.error("Falha ao carregar perfis. Tente novamente.");
      setSellers([]);
    } finally {
      setLoading(false);
    }
  }, [user?.token, submittedSearch, filters.role, filters.disabled]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = useCallback((page: number) => {
    setPagination((p) => ({ ...p, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination((p) => ({ ...p, pageSize, page: 1 }));
  }, []);

  const handleOpenCreateModal = () => {
    setEditingSeller(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = useCallback((id: string) => {
    const sellerToEdit = sellers.find((s) => s.id === id);
    if (sellerToEdit) {
      setEditingSeller(sellerToEdit);
      setIsModalOpen(true);
    }
  }, [sellers]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSeller(null);
  };

  const handleSaveSeller = async (sellerData: SellerFormData) => {
    const payload = buildSellerPayload(sellerData);

    // Em erro o serviço já mostrou o motivo e devolve null: a janela fica aberta
    // para corrigir. Antes o "atualizado com sucesso" saía mesmo quando falhava.
    if (sellerData.id) {
      const res = await SellersService.updateSeller(
        sellerData.id,
        payload,
        user?.token || "",
      );
      if (!res) return;
      toast.success("Usuário atualizado com sucesso");
    } else {
      const res = await SellersService.createSeller(payload, user?.token || "");
      if (!res) return;
      toast.success(res.data?.message || "Usuário criado com sucesso");
    }

    await fetchData();
    handlePageChange(1);
    handleCloseModal();
  };

  const handleDeleteClick = useCallback((id: string) => {
    setSellerToDeleteId(id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = async () => {
    let ok = false;
    if (sellerToDeleteId !== null) {
      ok = (await SellersService.deleteUser(sellerToDeleteId, user?.token || "")) !== null;
    }
    await fetchData();
    handlePageChange(1);
    setIsDeleteModalOpen(false);
    setSellerToDeleteId(null);
    if (ok) toast.success("Usuário excluído com sucesso");
  };

  /**
   * Ativa ou desativa usuários pelo mesmo PUT /users/{id} do formulário (isActive).
   * Antes, os botões "Ativar/Desativar" em lote só faziam console.log (bug reportado em 01/10/2026).
   */
  const alterarStatus = useCallback(
    async (ids: (string | number)[], ativar: boolean) => {
      if (!user?.token || ids.length === 0) return;
      const alvos = sellers.filter((s) => ids.includes(s.id) && (s.status === "disabled") === ativar);
      if (alvos.length === 0) {
        toast.info(ativar ? "Os usuários selecionados já estão ativos." : "Os usuários selecionados já estão desativados.");
        setSelectedRowIds([]);
        return;
      }
      // Em lote eram dezenas de PUTs em fila sem nenhum retorno na tela (~20 s para
      // 76 usuários), e parecia que o botão não funcionava. Agora vão 5 por vez com aviso.
      const aviso = alvos.length > 1
        ? toast.info(`${ativar ? "Ativando" : "Desativando"} ${alvos.length} usuários...`, { autoClose: false })
        : null;
      let feitos = 0;
      for (let i = 0; i < alvos.length; i += 5) {
        const resultados = await Promise.all(
          alvos.slice(i, i + 5).map((alvo) =>
            SellersService.updateSeller(
              alvo.id,
              { name: alvo.name, email: alvo.email, userType: alvo.userType, disabled: false, isActive: ativar, profileAccesses: null },
              user.token,
            ),
          ),
        );
        feitos += resultados.filter((r) => r !== null).length;
      }
      if (aviso !== null) toast.dismiss(aviso);
      setSelectedRowIds([]);
      await fetchData();
      if (feitos === alvos.length) {
        toast.success(`${feitos} usuário(s) ${ativar ? "ativado(s)" : "desativado(s)"}.`);
      } else {
        toast.warn(`${feitos} de ${alvos.length} ${ativar ? "ativado(s)" : "desativado(s)"}; veja o erro acima.`);
      }
    },
    [sellers, user?.token, fetchData],
  );

  const handleSelectRow = (rowId: string | number) => {
    setSelectedRowIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRowIds(checked ? sellers.map((s) => s.id) : []);
  };

  const columns: DataGridColumn[] = [
    { id: "name", header: "Usuário", minWidth: 280 },
    { id: "level", header: "Nível de acesso", minWidth: 170 },
    { id: "status", header: "Status", minWidth: 110 },
    { id: "lastLogin", header: "Último acesso", minWidth: 140, hideOnMobile: true },
    { id: "actions", header: "Ações", align: "right", minWidth: 150 },
  ];

  const tomDoNivel = (level: Seller["level"]) =>
    level === "Admin" ? "red"
    : level === "Supervisor de Contas" ? "brand"
    : level === "Vendedor" ? "green"
    : level === "Conta Google (importador)" ? "yellow"
    : "grey";

  const ultimoAcesso = (iso?: string | null) => {
    if (!iso) return "Nunca acessou";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? "—"
      : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const rows: DataGridRow[] = useMemo(() => {
    const start = (pagination.page - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    const paginatedSellers = sellers.slice(start, end);

    return paginatedSellers.map((seller) => ({
      id: seller.id,
      render: {
        name: (
          <NameCell
            primaryText={seller.name}
            secondaryText={seller.email}
            avatar={iniciais(seller.name)}
            avatarTone={tomDoNivel(seller.level)}
          />
        ),
        level: <LevelChip level={seller.level} />,
        lastLogin: (
          <Typography variant="body2" color="text.secondary">
            {ultimoAcesso(seller.lastLoginAt)}
          </Typography>
        ),
        status: (
          <StatusChip status={seller.status} label={seller.statusLabel} />
        ),
        actions: (
          <RowActionButtons
            actions={[
              {
                icon: <ModeEditOutlineIcon fontSize="small" />,
                tooltip: "Editar",
                onClick: () => handleOpenEditModal(seller.id),
              },
              {
                icon: seller.status === "disabled" ? <PersonOutlineIcon fontSize="small" /> : <PersonOffOutlinedIcon fontSize="small" />,
                tooltip: seller.status === "disabled" ? "Ativar" : "Desativar",
                onClick: () => alterarStatus([seller.id], seller.status === "disabled"),
              },
              {
                icon: (
                  <DeleteOutlineIcon
                    fontSize="small"
                    sx={{ color: "#D93025" }}
                  />
                ),
                tooltip: "Excluir",
                onClick: () => handleDeleteClick(seller.id),
              },
            ]}
          />
        ),
      },
    }));
  }, [sellers, pagination.page, pagination.pageSize, handleOpenEditModal, handleDeleteClick, alterarStatus]);

  const filterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (filters.role && filters.role !== "") {
      chips.push({
        id: "role",
        label: `Nível de acesso: ${parseUserType(filters.role)}`,
        onDelete: () => handleFilterChange("role", ""),
      });
    }
    if (filters.disabled !== null) {
      chips.push({
        id: "disabled",
        label: `Status: ${filters.disabled ? "Desativado" : "Ativado"}`,
        onDelete: () => handleFilterChange("disabled", null),
      });
    }
    return chips;
  }, [filters]);

  const handleFilterChange = (field: string, value: unknown) => {
    if (field === "role" && typeof value === "string") {
      setFilters((prev) => ({ ...prev, role: value }));
    }
    if (field === "disabled" && (typeof value === "boolean" || value === null)) {
      setFilters((prev) => ({ ...prev, disabled: value }));
    }
    // Aqui você chamaria a API para buscar os dados filtrados
    console.log(`Filtering by ${field}: ${value}`);
  };
  const handleClearFilters = () => {
    setFilters(initialFilters);
    // Aqui você chamaria a API para buscar os dados sem filtros
    console.log("Clearing filters");
  };

  const chipFilters: ChipFilterConfig[] = [
    {
      id: "role",
      label: "Nível de Acesso",
      options: [
        { label: "Todos", value: "" },
        { label: "Cliente", value: "CUSTOMER" },
        { label: "Supervisor de Contas", value: "PORTFOLIO_MANAGER" },
        { label: "Conta Google (importador)", value: "GMAIL_MANAGER" },
        { label: "Admin", value: "ADMIN" },
        { label: "Vendedor", value: "STAFF" },
      ],
    },
    {
      id: "disabled",
      label: "Status",
      options: [
        { label: "Todos", value: null },
        { label: "Ativado", value: false },
        { label: "Desativado", value: true },
      ],
    },
  ];

  const handleSearchClick = useCallback(() => {
    // aplica a busca e volta para a primeira página
    setPagination((p) => ({ ...p, page: 1 }));
    setSubmittedSearch(searchTerm);
  }, [searchTerm]);

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title="Equipe e acessos"
          subtitle="Quem entra no sistema e com qual nível. Contas Google (importador) só importam perfis; para vê-las, use Mais filtros › Nível de acesso."
          actions={
            <FilledButton type="button" onClick={handleOpenCreateModal}>
              <span className="material-icons">person_add</span>
              Novo usuário
            </FilledButton>
          }
        />
        <Box sx={{ width: "calc(100% - 64px)", mt: 3 }}>
          <KpiStrip
            aria-label="Usuários por nível"
            items={[
              { icon: "group", value: formatarNumero(sellers.length), label: "usuários na lista", tone: "brand" },
              { icon: "admin_panel_settings", value: formatarNumero(contarNivel("Admin")), label: "admins", tone: "red" },
              { icon: "supervisor_account", value: formatarNumero(contarNivel("Supervisor de Contas")), label: "supervisores", tone: "brand" },
              { icon: "badge", value: formatarNumero(contarNivel("Vendedor")), label: "vendedores", tone: "green" },
              { icon: "person_off", value: formatarNumero(sellers.filter((u) => u.status === "disabled").length), label: "desativados", tone: "grey" },
            ]}
          />
        </Box>
        <Box
          sx={{ width: "calc(100% - 64px)", marginBottom: isMobile() ? "6vh" : "1px", mt: 3 }}
        >
          <AppDataGrid
            toolbarChips={
              <>
                <ToolbarDivider />
                <ChipGroupLabel>Nível</ChipGroupLabel>
                {[
                  { label: "Todos", value: "" },
                  { label: "Admin", value: "ADMIN" },
                  { label: "Supervisor", value: "PORTFOLIO_MANAGER" },
                  { label: "Vendedor", value: "STAFF" },
                  { label: "Cliente", value: "CUSTOMER" },
                ].map((opcao) => (
                  <ChipButton
                    key={opcao.value}
                    type="button"
                    $on={(filters.role ?? "") === opcao.value}
                    aria-pressed={(filters.role ?? "") === opcao.value}
                    onClick={() => handleFilterChange("role", opcao.value)}
                  >
                    {opcao.label}
                  </ChipButton>
                ))}
                <ToolbarDivider />
                <ChipGroupLabel>Status</ChipGroupLabel>
                {[
                  { label: "Todos", value: null, dot: null },
                  { label: "Ativado", value: false, dot: "#1E8E3E" },
                  { label: "Desativado", value: true, dot: "#9AA0A6" },
                ].map((opcao) => (
                  <ChipButton
                    key={String(opcao.value)}
                    type="button"
                    $on={filters.disabled === opcao.value}
                    aria-pressed={filters.disabled === opcao.value}
                    onClick={() => handleFilterChange("disabled", opcao.value)}
                  >
                    {opcao.dot && <span className="dot" style={{ background: opcao.dot }} />}
                    {opcao.label}
                  </ChipButton>
                ))}
              </>
            }
            resultsLabel={`${formatarNumero(sellers.length)} usuários`}
            columns={columns}
            rows={rows}
            tableLabel="usuários"
            selectedRowIds={selectedRowIds}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onOpenFilters={() => setIsFilterDrawerOpen(true)}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            searchPlaceholder="Buscar usuário..."
            filterChips={filterChips}
            filterCount={filterChips.length}
            onSearchClick={handleSearchClick}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            bulkActionsBarRenderer={(selectedIds) => {
              const bulkActions: BulkAction[] = [
                {
                  label: "Ativar",
                  onClick: () => alterarStatus(selectedIds, true),
                },
                {
                  label: "Desativar",
                  onClick: () => alterarStatus(selectedIds, false),
                },
                {
                  label: "Cancelar",
                  onClick: () => setSelectedRowIds([]),
                  color: "secondary",
                },
              ];
              return (
                <BulkActionsBar
                  selectedCount={selectedIds.length}
                  tableLabel="usuário"
                  actions={bulkActions}
                />
              );
            }}
            noDataIcon={<SearchIcon sx={{ fontSize: 48 }} />}
            noDataTitle="Nenhum usuário encontrado"
            noDataDescription="Ajuste sua busca ou adicione novos usuários."
            isLoading={loading}
          />
        </Box>
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          chipFilters={chipFilters}
        />
        <SellerFormModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveSeller}
          seller={editingSeller}
        />
        <ConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Confirmar Exclusão"
          message="Tem certeza de que deseja excluir este usuário? Esta ação não pode ser desfeita."
          confirmButtonText="Excluir"
        />
      </Container>
    </Page.Root>
  );
};
